import { useState } from 'react';
import { Autocomplete, TextField, Chip, CircularProgress, Box, Typography } from '@mui/material';
import { Building2, User, Briefcase, Target, FileText } from 'lucide-react';
import { useGlobalSearch } from '@lingaai/shared-hooks';
import { ICON_SIZES, ICON_STROKE } from '../../constants/icons';

interface RelatedEntity {
  id: string;
  type: string;
  name: string;
}

interface EntityMultiSelectProps {
  value: RelatedEntity[];
  onChange: (entities: RelatedEntity[]) => void;
  label?: string;
  placeholder?: string;
  allowedTypes?: string[];
}

const TYPE_ICONS: Record<string, React.ElementType> = {
  Person: User,
  Organization: Building2,
  Deal: Briefcase,
  Opportunity: Target,
  Activity: FileText,
};

const TYPE_COLORS: Record<string, string> = {
  Person: '#4CAF50',
  Organization: '#2196F3',
  Deal: '#FF9800',
  Opportunity: '#9C27B0',
  Activity: '#607D8B',
};

function getEntityIcon(type: string) {
  const Icon = TYPE_ICONS[type] || FileText;
  return <Icon size={ICON_SIZES.sm} strokeWidth={ICON_STROKE} />;
}

function getEntityColor(type: string) {
  return TYPE_COLORS[type] || '#757575';
}

export function EntityMultiSelect({ value, onChange, label = 'Related Entities', placeholder = 'Search entities...', allowedTypes }: EntityMultiSelectProps) {
  const [inputValue, setInputValue] = useState('');
  const { data: searchResults, isLoading } = useGlobalSearch(inputValue, { pageSize: 20 });

  const filteredResults =
    searchResults?.filter((result) => {
      if (allowedTypes && !allowedTypes.includes(result.type)) return false;
      return !value.some((v) => v.id === result.id);
    }) || [];

  const options: RelatedEntity[] = filteredResults.map((result) => ({
    id: result.id,
    type: result.type,
    name: (result.props?.name as string) || (result.props?.subject as string) || (result.props?.title as string) || 'Unknown',
  }));

  return (
    <Autocomplete
      multiple
      options={options}
      value={value}
      onChange={(_event, newValue) => onChange(newValue)}
      inputValue={inputValue}
      onInputChange={(_event, newInputValue) => setInputValue(newInputValue)}
      getOptionLabel={(option) => option.name}
      isOptionEqualToValue={(option, val) => option.id === val.id}
      loading={isLoading}
      filterOptions={(x) => x}
      renderInput={(params) => (
        <TextField
          {...params}
          label={label}
          placeholder={value.length === 0 ? placeholder : ''}
          size="small"
          InputProps={{
            ...params.InputProps,
            endAdornment: (
              <>
                {isLoading ? <CircularProgress color="inherit" size={20} /> : null}
                {params.InputProps.endAdornment}
              </>
            ),
          }}
        />
      )}
      renderOption={(props, option) => {
        const { key, ...otherProps } = props;
        return (
          <Box component="li" key={key} {...otherProps} sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Box sx={{ color: getEntityColor(option.type), display: 'flex', alignItems: 'center' }}>{getEntityIcon(option.type)}</Box>
            <Box>
              <Typography variant="body2">{option.name}</Typography>
              <Typography variant="caption" color="text.secondary">
                {option.type}
              </Typography>
            </Box>
          </Box>
        );
      }}
      renderTags={(tagValue, getTagProps) =>
        tagValue.map((option, index) => {
          const { key, ...tagProps } = getTagProps({ index });
          return (
            <Chip
              key={key}
              {...tagProps}
              label={option.name}
              size="small"
              icon={<Box sx={{ color: getEntityColor(option.type), display: 'flex', alignItems: 'center', ml: 0.5 }}>{getEntityIcon(option.type)}</Box>}
              sx={{
                '& .MuiChip-icon': {
                  marginLeft: '4px',
                  marginRight: '-4px',
                },
              }}
            />
          );
        })
      }
    />
  );
}
